export const APP_BACKGROUND_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const DEFAULT_APP_BACKGROUND_COLOR = '#ffffff';
export const APP_BACKGROUND_FILTER_FIELDS = {
  grayscale: { defaultValue: 0, min: 0, max: 1 },
  invert: { defaultValue: 0, min: 0, max: 1 },
  brightness: { defaultValue: 1, min: 0, max: 10 }
};
export const APP_BACKGROUND_TRANSFORM_FIELDS = {
  translateX: { defaultValue: 0, min: -24, max: 24 },
  translateY: { defaultValue: 0, min: -24, max: 24 }
};
export const APP_BACKGROUND_NUMERIC_PROPERTY_FIELDS = {
  ...APP_BACKGROUND_FILTER_FIELDS,
  ...APP_BACKGROUND_TRANSFORM_FIELDS
};
export const DEFAULT_APP_BACKGROUND_PROPERTIES = {
  backgroundColor: null,
  grayscale: APP_BACKGROUND_FILTER_FIELDS.grayscale.defaultValue,
  invert: APP_BACKGROUND_FILTER_FIELDS.invert.defaultValue,
  brightness: APP_BACKGROUND_FILTER_FIELDS.brightness.defaultValue,
  translateX: APP_BACKGROUND_TRANSFORM_FIELDS.translateX.defaultValue,
  translateY: APP_BACKGROUND_TRANSFORM_FIELDS.translateY.defaultValue
};

export const isAppBackgroundHex = (value) => {
  return typeof value === 'string' && APP_BACKGROUND_HEX_PATTERN.test(value);
};

export const resolveAppBackgroundColor = (value, fallback = DEFAULT_APP_BACKGROUND_COLOR) => {
  if (isAppBackgroundHex(value)) {
    return value;
  }

  return fallback;
};

export const normalizeAppBackgroundHexInput = (value) => {
  const hex = String(value || '')
    .trim()
    .replace(/^#+/, '')
    .replace(/#/g, '')
    .replace(/[^0-9a-fA-F]/g, '')
    .slice(0, 6);

  if (!hex) {
    return '';
  }

  return `#${hex}`;
};

const normalizeAppBackgroundNumericValue = ({ key, value, fields, type }) => {
  const field = fields[key];
  if (!field) {
    return { success: false, error: `${key} is not a supported app background ${type}.` };
  }

  if (value === undefined || value === null || value === '') {
    return { success: true, value: field.defaultValue };
  }

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue < field.min || numberValue > field.max) {
    return { success: false, error: `${key} must be a number from ${field.min} to ${field.max}.` };
  }

  return { success: true, value: numberValue };
};

export const normalizeAppBackgroundFilterValue = ({ key, value }) => normalizeAppBackgroundNumericValue({ key, value, fields: APP_BACKGROUND_FILTER_FIELDS, type: 'filter' });

export const normalizeAppBackgroundTransformValue = ({ key, value }) => normalizeAppBackgroundNumericValue({ key, value, fields: APP_BACKGROUND_TRANSFORM_FIELDS, type: 'transform' });

export const normalizeAppBackgroundPropertyValue = ({ key, value }) => normalizeAppBackgroundNumericValue({ key, value, fields: APP_BACKGROUND_NUMERIC_PROPERTY_FIELDS, type: 'property' });

export const buildAppBackgroundFilter = (properties = {}) => {
  return Object.keys(APP_BACKGROUND_FILTER_FIELDS)
    .map((key) => {
      const result = normalizeAppBackgroundFilterValue({ key, value: properties?.[key] });
      const value = result.success ? result.value : APP_BACKGROUND_FILTER_FIELDS[key].defaultValue;
      return `${key}(${value})`;
    })
    .join(' ');
};

export const buildAppBackgroundTransform = (properties = {}) => {
  return Object.keys(APP_BACKGROUND_TRANSFORM_FIELDS)
    .map((key) => {
      const result = normalizeAppBackgroundTransformValue({ key, value: properties?.[key] });
      const value = result.success ? result.value : APP_BACKGROUND_TRANSFORM_FIELDS[key].defaultValue;
      return `${key}(${value}px)`;
    })
    .join(' ');
};

export const buildAppLogoAppearance = ({ appBackgroundEnabled, appBackgroundProperties = {} } = {}) => {
  const isEnabled = appBackgroundEnabled === true;

  if (!isEnabled) {
    return {
      backgroundColor: null,
      filter: 'none',
      hasBackground: false,
      isEnabled: false,
      transform: 'none'
    };
  }

  const backgroundColor = isAppBackgroundHex(appBackgroundProperties?.backgroundColor) ? appBackgroundProperties.backgroundColor : null;

  return {
    backgroundColor,
    filter: buildAppBackgroundFilter(appBackgroundProperties),
    hasBackground: Boolean(backgroundColor),
    isEnabled: true,
    transform: buildAppBackgroundTransform(appBackgroundProperties)
  };
};
